import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder'
import { Vector3 } from '@babylonjs/core/Maths/math.vector'
import type { Mesh } from '@babylonjs/core/Meshes/mesh'
import type { Scene } from '@babylonjs/core/scene'
import type { MaterialLibrary } from '../rendering/MaterialLibrary'

interface Fragment {
  readonly mesh: Mesh
  readonly initialVelocity: Vector3
  readonly velocity: Vector3
}

export class DeathEffect {
  private readonly fragments: readonly Fragment[]
  private active = false
  private age = 0

  public constructor(scene: Scene, materials: MaterialLibrary) {
    this.fragments = Array.from({ length: 22 }, (_, index) => {
      const mesh = MeshBuilder.CreatePolyhedron(`death-fragment-${index}`, { type: index % 5, size: 0.22 }, scene)
      mesh.material = index % 3 === 0 ? materials.obstacle : materials.edge
      mesh.setEnabled(false)
      const angle = index / 22 * Math.PI * 2
      const initialVelocity = new Vector3(
        Math.cos(angle) * (3 + index % 4),
        2 + index % 5,
        Math.sin(angle) * (3 + index % 3),
      )
      return { mesh, initialVelocity, velocity: initialVelocity.clone() }
    })
  }

  public trigger(position: Readonly<Vector3>): void {
    this.active = true
    this.age = 0
    for (const fragment of this.fragments) {
      fragment.mesh.position.copyFrom(position)
      fragment.mesh.rotation.setAll(0)
      fragment.mesh.scaling.setAll(1)
      fragment.velocity.copyFrom(fragment.initialVelocity)
      fragment.mesh.setEnabled(true)
    }
  }

  public update(deltaSeconds: number): void {
    if (!this.active) return
    this.age += deltaSeconds
    for (const fragment of this.fragments) {
      fragment.velocity.y -= 18 * deltaSeconds
      fragment.mesh.position.addInPlace(fragment.velocity.scale(deltaSeconds))
      fragment.mesh.rotation.x += deltaSeconds * 5
      fragment.mesh.rotation.y += deltaSeconds * 7
      fragment.mesh.scaling.setAll(Math.max(0, 1 - this.age * 1.8))
    }
    if (this.age > 0.58) {
      this.active = false
      for (const fragment of this.fragments) fragment.mesh.setEnabled(false)
    }
  }
}
